import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOMServer from 'react-dom/server';
import Chat from '../../components/app/chat/Chat';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ChatMessage } from '../../types/backend/SengiLabsTypes';
import { toChatMessage } from '../../hooks/ChatHooks';
import { ApiPortalBackend, PortalConversation, PortalWorkspaceMember } from '../../apis/ApiPortalBackend';
import { useUserHook } from '../../hooks/UserHook';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faCopy,faEllipsis,faFilePdf,faListUl, faPenToSquare, faShareNodes, faTrash } from '@fortawesome/free-solid-svg-icons'
import { Link, useParams } from 'react-router-dom';
import hljs from "highlight.js";
import html2pdf from 'html2pdf.js';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; //direkte Unterstützung für Fußnoten, Durchstreichungen, Tabellen, Aufgabenlisten und URLs
import rehypeKatex from 'rehype-katex'; //um Mathematik darzustellen
import remarkMath from 'remark-math'; //Mathematik in Markdown zu unterstützen
import rehypeRaw from 'rehype-raw'; // HTML in markdown
import rehypeStringify from 'rehype-stringify';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeFormat from 'rehype-format';
import remarkEmoji from 'remark-emoji'; //display short cut emojis
import remarkCollapse from 'remark-collapse'; //not directly typed, make collapsable sections
import mermaid from "mermaid";
import { SengiColors } from '../../utils/SengiColors';

//TODO: Adjust total pages based on viewMode
export const ChatHistory = () => {
    const { t } = useTranslation('chat');
    const [user] = useUserHook();
    const [historyCollapsed, setHistoryCollapsed] = useState(true);
    // Teilen-Picker: statt roher User-ID-Eingabe (window.prompt) werden die Workspace-Mitglieder
    // aus GET /v1/workspaces/:id angeboten. sharingChatId != null oeffnet den Dialog.
    const [sharingChatId, setSharingChatId] = useState<string|null>(null);
    const [shareMembers, setShareMembers] = useState<PortalWorkspaceMember[]|null>(null);
    const [shareRole, setShareRole] = useState<'viewer'|'editor'>('viewer');
    const [shareStatus, setShareStatus] = useState<{kind:'success'|'danger';text:string}|null>(null);
    const [shareBusy, setShareBusy] = useState(false);
    const { effectiveTheme } = useBootstrapTheme();
    const isMobile = useIsMobile();
    const [selectedChat, setSelectedChat] = useState<PortalConversation|null>(null);
    const [userChats, setUserChats] = useState<PortalConversation[]>([]);
    const [filteredChats, setFilteredChats] = useState<PortalConversation[]>([]);
    const [searchQuery, setSearchQuery] = useState('');

    const sortByRecent = (chats: PortalConversation[]) =>
        [...chats].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    const loadUserChats = async() => {
        const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
        if (!workspaceId) return;
        const chats = await ApiPortalBackend.GetChats(workspaceId);
        const sorted = sortByRecent(chats ?? []);
        setUserChats(sorted);
        setFilteredChats(sorted);
    };

    const searchInChats = () => {
        const query = searchQuery.toLowerCase();
        // Nur Titel-Suche - Nachrichteninhalte werden fuer die Liste nicht mehr geladen (kein
        // Preview-Snippet mehr, siehe TASKS.md „Chat-History an echte Conversations-API").
        let newFilteredChats = userChats.filter(chat => chat.title?.toLowerCase().includes(query));
        setFilteredChats(newFilteredChats);
    };

    const addNewChat = async() => {
        const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
        if (!workspaceId) return;
        const newChat = await ApiPortalBackend.CreateConversation(workspaceId, 'chat');
        if (!newChat) return;
        const newUserChats = [newChat, ...userChats];
        setUserChats(newUserChats);
        setFilteredChats(newUserChats);
        setSelectedChat(newChat);
        if (isMobile) setHistoryCollapsed(true);
    };
    const deleteChat = async(chatID:string) => {
        const ok = await ApiPortalBackend.DeleteConversation(chatID);
        if (!ok) return;
        const newChats = userChats.filter(item => item.id !== chatID);
        setUserChats(newChats);
        setFilteredChats(newChats);
        if (selectedChat?.id === chatID) setSelectedChat(null);
    };
    // Teilen-Dialog oeffnen und die Workspace-Mitglieder laden (GET /v1/workspaces/:id). Der
    // eingeloggte Nutzer selbst wird aus der Liste gefiltert (mit sich teilen ergibt keinen Sinn).
    const openShare = async(chatID:string) => {
        setSharingChatId(chatID);
        setShareMembers(null);
        setShareStatus(null);
        setShareRole('viewer');
        const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
        if (!workspaceId) { setShareMembers([]); return; }
        const ws = await ApiPortalBackend.GetWorkspace(workspaceId);
        if (!ws) { setShareStatus({ kind: 'danger', text: t('history.share.loadFailed') }); setShareMembers([]); return; }
        const own = user?.user.id;
        const members = (ws.members ?? []).filter(m => String(m.user?.id ?? m.userId) !== String(own));
        setShareMembers(members);
    };
    const doShare = async(member: PortalWorkspaceMember) => {
        if (!sharingChatId) return;
        setShareBusy(true);
        const ok = await ApiPortalBackend.ShareConversation(sharingChatId, member.userId, shareRole);
        setShareBusy(false);
        setShareStatus({ kind: ok ? 'success' : 'danger', text: ok ? t('history.share.success') : t('history.share.failed') });
        if (ok) setTimeout(() => setSharingChatId(null), 1200);
    };
    const memberLabel = (m: PortalWorkspaceMember) =>
        m.user?.displayName || m.user?.username || m.user?.email || m.userId;
    const changeChatTitle = async(chatID:string) => {
        const current = userChats.find(item => item.id === chatID);
        const newTitle = window.prompt(t('history.renamePrompt'), current?.title ?? '');
        if (!newTitle) return;
        const updated = await ApiPortalBackend.UpdateConversation(chatID, { title: newTitle });
        if (!updated) return;
        const applyTitle = (chats: PortalConversation[]) =>
            chats.map(item => item.id === chatID ? updated : item);
        setUserChats(applyTitle);
        setFilteredChats(applyTitle);
        if (selectedChat?.id === chatID) setSelectedChat(updated);
    };
    const renderMermaidAsBase64 = async(mermaidCode: string): Promise<string> => {
        //TODO: fix image return and creation
        try {
            const { svg } = await mermaid.render('diagram-' + Math.random().toString(36).substring(2), mermaidCode);
            // Canvas erzeugen
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // SVG als Blob → URL
            const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);

            // Image laden
            const img = new Image();
            img.src = url;

            await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject();
            });

            // Canvas-Größe an Bild anpassen
            canvas.width = img.width;
            canvas.height = img.height;
            // Bild auf Canvas zeichnen
            if(ctx){
                ctx.drawImage(img, 0, 0);
            }
            // PNG Base64 erzeugen
            // eslint-disable-next-line no-restricted-syntax -- rendert ein Mermaid-Diagramm (UI-Grafik), kein KI-generiertes Medium, Art. 50 KI-VO nicht einschlaegig
            const pngBase64 = canvas.toDataURL("image/png");

            URL.revokeObjectURL(url);
            return pngBase64;
        } catch (err) {
            console.error("Mermaid → PNG Fehler", err);
            return "";
        }
    };

    const messageToHTMLContent = async(message: ChatMessage) => {
        let i = 0
        for(let item of message.contents){
            if(item.type == 'mermaid'){
                let baseImg = await renderMermaidAsBase64(item.element);
                message.contents[i].element = baseImg;
            }
            i++;
        };
        let content = ReactDOMServer.renderToStaticMarkup(
            <div>
                {message.contents.map(x => (
                    <>
                        {   x.type === 'text'
                            ?   <div className='row'>
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkMath,remarkEmoji,remarkGfm, remarkParse,remarkRehype,[remarkCollapse,{test:'heading'}]]} 
                                        rehypePlugins={[rehypeKatex,rehypeFormat,rehypeRaw, rehypeStringify]}
                                    >
                                            {x.element}
                                    </ReactMarkdown>
                                </div>
                            : x.type === 'image'
                                ?   <p>
                                        <img 
                                            src={x.element} 
                                            className={'rounded my-auto mx-auto d-block'}
                                            style={{height:'150px', width: 'auto'}}
                                        />
                                    </p>
                                : x.type === 'video'
                                    ?   <p>
                                            <video src={x.element} controls style={{
                                                maxWidth:'100%',
                                                margin: '5px 0'
                                            }}></video>
                                        </p>
                                    : x.type === 'code'
                                        ?   <p>
                                                <pre style={{
                                                    background: '#1e1e1e',
                                                    color: '#f8f8f2',
                                                    padding: '10px',
                                                    borderRadius: '4px'
                                                        }}
                                                    >
                                                    <code className={x.codeClass ?? ''}>
                                                        {x.element}
                                                    </code>
                                                </pre>
                                            </p>
                                        : x.type === 'mermaid'
                                            ? <img 
                                                    src={x.element} 
                                                    className={'my-auto mx-auto d-block'}
                                                    style={{height:'150px', width: 'auto'}}
                                                />
                                            : x.type == 'pdf'
                                                ? <p>[{t('history.pdfAttachment')}: <a href={x.element} target="_blank">{t('history.download')}</a>]</p>
                                                : <></>
                        }
                    </>
                ))}
            </div>
        )
        return content;
    };
    // Die Liste liefert keine Nachrichten mehr (nur Titel/updatedAt, siehe loadUserChats) -
    // Copy/PDF laden sie bei Bedarf direkt nach.
    const loadChatMessages = async (chatID: string): Promise<ChatMessage[]> => {
        const messages = await ApiPortalBackend.GetConversationMessages(chatID);
        return messages ? messages.map(toChatMessage) : [];
    };
    const downloadPDF = async (chatData:PortalConversation) => {
        const messages = await loadChatMessages(chatData.id);
        let fontFamily = 'Helvetica';
        let filename = `chat_${chatData.title}_${uuidv4()}`;
        const messageContents = await Promise.all(
            messages.map(async (message) => {
                const author = message.role === 'user'
                    ? '<strong>You:</strong> '
                    : '<strong>Assistant:</strong> ';
                
                const messageHTML = await messageToHTMLContent(message);
                return `<div style="margin-bottom:12px;">${author}${messageHTML}</div>`;
            })
        );
        const content = messageContents.join('\n');
        const htmlContent = `
         <div style="
            font-family: ${fontFamily};
            padding: 24px;
            line-height: 1.6;
            font-size: 14px;
            color: #222;
            ">
            <h2>${chatData.title}</h2>
            ${content}
        </div>
        `
        const options = {
            margin: 0.5,
            filename: `${filename}.pdf`,
            image: { type: 'jpeg', quality: 0.98 } as const,
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'a4'},
        };
        
        html2pdf().set(options).from(htmlContent).save();
    };
    const copyChat = async (chatData:PortalConversation) => {
        const messages = await loadChatMessages(chatData.id);
        let copiedText = ''
        for(let message of messages){
            let author = '';
            if(message.role === 'user'){
            author =  'You: ';
            }
            else{
            author = 'Assistant: ';
            }
            let content = author + message.contents.map(x => x.type === 'text' ? x.element : x.element.toString()).join('\n ');
            copiedText += content + '\n\n';
        }
        navigator.clipboard.writeText(copiedText);
        let copiedToast = document.getElementById("CopiedTextSuccesfull");
        if(!copiedToast) return;
        copiedToast.style.display = 'block';
        setTimeout(() => {
            if(copiedToast){
            copiedToast.style.display = 'none';
            }
        }, 1000);
    };

    useEffect(() => {
        loadUserChats();
        mermaid.initialize({ 
            startOnLoad: true,
            // theme: "default",//base, dark,default,forest,neutral
            securityLevel: "loose",
            //https://github.com/mermaid-js/mermaid/blob/develop/packages/mermaid/src/docs/config/theming.md
            themeVariables: {
                primaryTextColor: SengiColors.sng_charcoal,//sng-charcoal
                primaryBorderColor: SengiColors.sng_veilchen,//sng-veillchen,
                lineColor: SengiColors.sng_flieder,//$sng-flieder,
                background: 'rgba(228,204,255,0.4)',
                fontFamily: 'arial',
                secondaryColor: '#2196f3',
                // edgeLabelBackground: '#fdccff',
            },
            themeCSS: `
            .node rect {
                fill: 'rgba(228,204,255,0.4)' !important;;
                stroke: ${SengiColors.sng_veilchen} !important;;
                stroke-width: 1px !important;;
            }
            `
         });

        mermaid.contentLoaded();
    }, []);

    // Sidebar defaults to visible on desktop, hidden on mobile (the previous inline
    // window.innerWidth-check here had the two branches swapped - visible/hidden were backwards
    // from what the comments said, see TASKS.md Task #77). Only reacts when the mobile/desktop
    // breakpoint is actually crossed, so a manual toggle via the hamburger button survives minor
    // resizes.
    useEffect(() => {
        setHistoryCollapsed(isMobile);
    }, [isMobile]);

    useEffect(()=>{
        if(searchQuery !== ""){
            searchInChats();
        }
        else if( userChats.length > 0 ){
            setFilteredChats(userChats)
        }
    },[searchQuery]);

    useEffect(()=>{
        setTimeout(()=>{
            hljs.highlightAll();
        },500)
    },[selectedChat]);

  return (
    <>
        <div className="container-fluid">
            <div className='row h-100'>
                {/* Sidebar - on mobile this becomes a fixed overlay drawer with a backdrop
                    instead of squeezing into the row next to the chat content, which would
                    otherwise overflow on narrow viewports (Task #77, 2026-07-16). */}
                { historyCollapsed
                    ?   <></>
                    :   <>
                        { isMobile &&
                            <div
                                onClick={() => setHistoryCollapsed(true)}
                                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 999 }}
                            />
                        }
                        <div className='col-auto'
                            style={{
                            left: 0,
                            top: isMobile ? 0 : undefined,
                            bottom: isMobile ? 0 : undefined,
                            position: isMobile ? 'fixed' : 'relative',
                            width: isMobile ? 'min(85vw, 300px)' : undefined,
                            maxWidth: isMobile ? 'min(85vw, 300px)' : '256px',
                            zIndex: 1000,
                            transition: 'transform 0.3s ease-in-out, width 0.3s ease-in-out',
                            display: 'flex',
                            background: effectiveTheme === 'dark'
                                ? 'linear-gradient(180deg, rgba(20, 38, 80, 0.9) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(30, 41, 59, 0.9) 100%)'
                                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.95) 100%)',
                            backdropFilter: 'blur(12px)',
                            borderRight: effectiveTheme === 'dark'
                                ? '1px solid rgba(96, 165, 250, 0.2)'
                                : '1px solid rgba(59, 130, 246, 0.1)',
                            boxShadow: effectiveTheme === 'dark'
                                ? '4px 0 20px rgba(59, 130, 246, 0.1)'
                                : '4px 0 20px rgba(59, 130, 246, 0.05)'
                            }}
                        >
                            <nav >
                                <ul className="navbar-nav">
                                    <li className='nav-item text-muted mb-1 mt-2'>
                                        <button className='btn ps-1' onClick={() => {addNewChat()}}>
                                        📝 {t('history.newChat')}
                                        </button>
                                    </li>
                                    <li className='nav-item text-muted mb-4'>
                                        <div className="input-group">
                                            <span className="input-group-text p-1">🔍</span>
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder={t('history.search')}
                                                className="form-control p-1"
                                            />
                                        </div>
                                    </li>
                                    <li className='nav-space text-muted'></li>
                                    <li className='fw-bold mb-2 text-muted'>{t('history.title')}</li>
                                    { filteredChats.map((chatData, index) => (
                                        <li className={'nav-item'} key={chatData.id}>
                                            <div className='row align-items-center'>
                                                <div className='col'>
                                                    <button
                                                        key={chatData.id}
                                                        className={`nav-link d-flex align-items-center mb-1 p-1 ${
                                                        chatData.id == selectedChat?.id ? 'bg-primary bg-opacity-10 border border-primary rounded-2 border-opacity-25 text-primary' : 
                                                        'text-body-secondary border-0'
                                                        }`}
                                                        style={{
                                                        position: 'relative',
                                                        background:  chatData.id == selectedChat?.id
                                                            ? effectiveTheme === 'dark'
                                                            ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)'
                                                            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%)'
                                                            : 'transparent',
                                                        border:  chatData.id == selectedChat?.id
                                                            ? effectiveTheme === 'dark'
                                                            ? '1px solid rgba(96, 165, 250, 0.3)'
                                                            : '1px solid rgba(59, 130, 246, 0.2)'
                                                            : '1px solid transparent',
                                                        color:  chatData.id == selectedChat?.id
                                                            ? effectiveTheme === 'dark' ? '#60a5fa' : '#3b82f6'
                                                            : effectiveTheme === 'dark' ? '#cbd5e1' : '#64748b',
                                                        transition: 'all 0.2s ease',
                                                        boxShadow:  chatData.id == selectedChat?.id
                                                            ? effectiveTheme === 'dark'
                                                            ? '0 4px 12px rgba(96, 165, 250, 0.1)'
                                                            : '0 4px 12px rgba(59, 130, 246, 0.08)'
                                                            : 'none'
                                                        }}
                                                        title={chatData.title ?? `Chat ${index}`}
                                                        onClick={() => {
                                                            setSelectedChat(chatData);
                                                            if (isMobile) setHistoryCollapsed(true);
                                                        }}
                                                    >
                                                        {chatData.title || `Chat ${index + 1}`}
                                                    </button>
                                                </div>
                                                <div className='col-auto ps-1'>
                                                    <div className="dropdown">
                                                        <button 
                                                            className='hiddenButton px-1'
                                                            type="button" 
                                                            id="dropdownfunctionsSingleChat"
                                                            data-bs-toggle="dropdown"
                                                            aria-haspopup="true" 
                                                            aria-expanded="false"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEllipsis}
                                                                size='lg'
                                                                className='text-muted p-0'
                                                            />
                                                        </button>
                                                        <ul className="dropdown-menu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownfunctionsSingleChat" style={{textAlignLast:'start'}}>
                                                            <li className="dropdown-item text-light"
                                                                onClick={() => copyChat(chatData)}
                                                                data-bs-toggle="tooltip" 
                                                                data-bs-placement="top" 
                                                                title={t('history.copyChat')}
                                                                >
                                                                <FontAwesomeIcon
                                                                    icon={faCopy}
                                                                    className="me-2"
                                                                    size="lg"
                                                            />
                                                                Copy Chat
                                                            </li>
                                                            <li className="dropdown-item text-light"
                                                                onClick={() => downloadPDF(chatData)}
                                                                title={t('history.getPdf')}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faFilePdf}
                                                                    className="me-2"
                                                                    size="lg"
                                                                    
                                                                />
                                                                Get PDF
                                                            </li>
                                                            <li className="dropdown-item text-light"
                                                                onClick={() => openShare(chatData.id)}
                                                                title={t('history.shareText')}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon= {faShareNodes}
                                                                    size="lg"
                                                                    className="me-2"
                                                                />
                                                                Share Chat
                                                            </li>
                                                            <li className="dropdown-item text-light"
                                                                onClick={() => changeChatTitle(chatData.id)}
                                                                title={t('history.renameChat')}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon= {faPenToSquare}
                                                                    size="lg"
                                                                    className="me-2"
                                                                />
                                                                Rename
                                                            </li>
                                                            <li className="dropdown-item text-light"
                                                                onClick={()=> deleteChat(chatData.id)}
                                                                title={t('history.deleteChat')}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon= {faTrash}
                                                                    size="lg"
                                                                    className="me-2"
                                                                />
                                                                Delete Chat
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))
                                    }
                                </ul>
                            </nav>
                        </div>
                        </>
                }
                {/* Chat */}
                <div className='col h-100'>
                    <div className='row h-100'>
                        <div className='d-flex flex-column h-100'>
                            {/* Header */}
                            <div className="row mx-1">
                                <div className='col-auto p-2 pe-0'>
                                    <Link 
                                        to="/executions" 
                                        className="btn btn-outline-secondary btn-sm ms-3"
                                    >
                                    <FontAwesomeIcon
                                            icon={faArrowLeftLong}
                                            size="1x"
                                            title={t('history.backToExecutions')}
                                        />
                                    </Link>
                                </div>
                                <div className='col-auto py-2'>
                                    <button
                                        className="btn btn-outline-secondary btn-sm ms-1"
                                        onClick={() => setHistoryCollapsed(!historyCollapsed)}
                                        title={historyCollapsed ? 'Show chat history' : 'Hide chat history'}
                                    >
                                        <span>☰</span>
                                    </button>
                                </div>
                                <div className="col text-center">
                                    <div className='h4 mb-0'>
                                        {selectedChat ? (selectedChat.title || t('history.untitled')) : t('history.startNew')}
                                    </div>
                                </div>
                                { selectedChat
                                    ?   <div className='col-auto'>
                                            <div className="top-0 end-0 position-relative">
                                                <div className="dropdown dropstart">
                                                <button 
                                                    className="btn btn-chat bg-transparent text-secondary border-0" 
                                                    type="button" 
                                                    id="dropdownfunctionsChat"
                                                    data-bs-toggle="dropdown"
                                                    aria-haspopup="true" 
                                                    aria-expanded="false"
                                                >
                                                    <FontAwesomeIcon
                                                    icon={faListUl}
                                                    size="lg"
                                                    title={t('history.moreFunctions')}
                                                    />
                                                </button>
                                                <ul className="dropdown-menu dropdown-menu-dark ms-4 p-1 body-background border-dark rounded" aria-labelledby="dropdownfunctionsChat" style={{textAlignLast:'start'}}>
                                                    <li className="dropdown-item text-light"
                                                    onClick={()=> copyChat(selectedChat)}
                                                    data-bs-toggle="tooltip" 
                                                    data-bs-placement="top" 
                                                    title={t('history.copyChat')}
                                                    >
                                                    <FontAwesomeIcon
                                                        icon={faCopy}
                                                        className="me-2"
                                                        size="lg"
                                                    />
                                                    {t('history.copyChat')}
                                                    </li>

                                                    <li className="dropdown-item text-light">
                                                    <FontAwesomeIcon
                                                        icon= {faShareNodes}
                                                        size="lg"
                                                        className="me-2"
                                                    />
                                                    {t('history.sendChat')}
                                                    </li>
                                                </ul>
                                                </div>
                                            </div>
                                        </div>
                                    : <></>
                                }
                            </div>
                            <div className='flex-grow-1 overflow-hidden'>
                                { selectedChat
                                    ?   <div className='col h-100'>
                                            <Chat
                                                chatID={selectedChat.id}
                                            />
                                        </div>
                                    :   <div className='col h-100'>
                                        {/* TODO: New Chat */}
                                            <Chat
                                                chatID=''
                                            />
                                        </div>

                                }
                            </div>
                        </div>
                    </div>
                </div>
                
            </div>
        </div>
        {sharingChatId && (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                onClick={() => setSharingChatId(null)}
            >
                <div className="sengi-dialog text-body p-4" style={{ maxWidth: 420, width: '90%' }} onClick={e => e.stopPropagation()}>
                    <h5 className="mb-1">{t('history.share.title')}</h5>
                    <p className="small text-secondary">{t('history.share.description')}</p>

                    {shareStatus && (
                        <div className={`alert alert-${shareStatus.kind} py-2`}>{shareStatus.text}</div>
                    )}

                    <div className="mb-3">
                        <label className="form-label small mb-1">{t('history.share.role')}</label>
                        <select
                            className="form-select form-select-sm"
                            value={shareRole}
                            onChange={e => setShareRole(e.target.value as 'viewer' | 'editor')}
                        >
                            <option value="viewer">{t('history.share.roleViewer')}</option>
                            <option value="editor">{t('history.share.roleEditor')}</option>
                        </select>
                    </div>

                    {shareMembers === null ? (
                        <p className="small text-secondary">{t('history.share.loading')}</p>
                    ) : shareMembers.length === 0 ? (
                        <p className="small text-secondary">{t('history.share.noMembers')}</p>
                    ) : (
                        <ul className="list-group mb-3">
                            {shareMembers.map(m => (
                                <li key={m.userId} className="list-group-item d-flex justify-content-between align-items-center gap-2">
                                    <span className="text-truncate">{memberLabel(m)}</span>
                                    <button className="btn btn-sengi btn-sm flex-shrink-0" disabled={shareBusy} onClick={() => doShare(m)}>
                                        {t('history.share.shareWith')}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="d-flex justify-content-end">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setSharingChatId(null)}>
                            {t('history.share.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </>
  );
};

export default ChatHistory;